import React, { useState, useCallback } from 'react';
import type { QuickFillItem } from '../../../shared/types';
import type { BulkUpdateResult } from '../../../shared/services/quickFillRevitService';
import { useActiveDocumentRefresh } from '../../../shared/hooks/ActiveDocumentProvider';
import {
  buildFailedCellErrors,
  shouldPauseContinueForSaveReview,
} from '../lib/quickFillPageModel';

export interface UseQuickFillSubmitOptions {
  handleSubmit: () => Promise<{ result: BulkUpdateResult | null; warning?: string }>;
  quickFillData: QuickFillItem[];
  parameterToLabel: Map<string, string>;
  isTableLoading: boolean;
  isQuickFillSyncing: boolean;
  onContinue: () => void;
}

export interface UseQuickFillSubmitResult {
  isContinuing: boolean;
  pendingNavigation: (() => void) | null;
  saveResultDialog: { open: boolean; result: BulkUpdateResult | null; warning?: string };
  failedCellErrors: Record<string, string>;
  setSaveResultDialog: React.Dispatch<React.SetStateAction<{ open: boolean; result: BulkUpdateResult | null; warning?: string }>>;
  setFailedCellErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  submitQuickFillChanges: () => Promise<boolean>;
  handleContinueClick: () => Promise<void>;
  handleApplyClick: () => Promise<void>;
  setPendingNavigation: (nav: (() => void) | null) => void;
  setIsContinuing: (value: boolean) => void;
}

export function useQuickFillSubmit(options: UseQuickFillSubmitOptions): UseQuickFillSubmitResult {
  const {
    handleSubmit,
    quickFillData,
    parameterToLabel,
    isTableLoading,
    isQuickFillSyncing,
    onContinue,
  } = options;

  const [isContinuing, setIsContinuing] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const { adoptModelVersion } = useActiveDocumentRefresh();
  const [saveResultDialog, setSaveResultDialog] = useState<{
    open: boolean;
    result: BulkUpdateResult | null;
    warning?: string;
  }>({ open: false, result: null });
  const [failedCellErrors, setFailedCellErrors] = useState<Record<string, string>>({});

  const submitQuickFillChanges = useCallback(async (): Promise<boolean> => {
    if (isContinuing || isQuickFillSyncing || isTableLoading) {
      return false;
    }

    setIsContinuing(true);
    setSaveResultDialog({ open: false, result: null });
    setFailedCellErrors({});

    try {
      const outcome = await handleSubmit();
      const nextFailedCells = buildFailedCellErrors(
        outcome.result,
        quickFillData,
        parameterToLabel
      );
      setFailedCellErrors(nextFailedCells);

      if (shouldPauseContinueForSaveReview(outcome)) {
        setSaveResultDialog({ open: true, result: outcome.result, warning: outcome.warning });
        return false;
      }

      // ModelChanged no longer refetches on its own: adopt the version the host
      // committed so the post-apply reconciliation reloads authoritative data.
      adoptModelVersion(outcome.result?.modelVersion ?? null);
      setSaveResultDialog({ open: false, result: null });
      return true;
    } finally {
      setIsContinuing(false);
    }
  }, [adoptModelVersion, handleSubmit, isContinuing, isQuickFillSyncing, isTableLoading, parameterToLabel, quickFillData]);

  const handleContinueClick = useCallback(async () => {
    if (isContinuing || isQuickFillSyncing) {
      return;
    }
    if (isTableLoading) {
      setPendingNavigation(() => onContinue);
      return;
    }

    const shouldNavigate = await submitQuickFillChanges();
    if (shouldNavigate) {
      onContinue();
    }
  }, [isContinuing, isQuickFillSyncing, isTableLoading, onContinue, submitQuickFillChanges]);

  const handleApplyClick = useCallback(async () => {
    const saved = await submitQuickFillChanges();
    if (saved) {
      setSaveResultDialog({ open: false, result: null });
    }
  }, [submitQuickFillChanges]);

  return {
    isContinuing,
    pendingNavigation,
    saveResultDialog,
    failedCellErrors,
    setSaveResultDialog,
    setFailedCellErrors,
    submitQuickFillChanges,
    handleContinueClick,
    handleApplyClick,
    setPendingNavigation,
    setIsContinuing,
  };
}
